import { Component, HostListener, inject, Inject, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import {
	MatDialog,
	MatDialogRef,
	MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ErrorCodes } from '@fnModels/error-codes';
import { Message } from '@fnModels/stewart';
import { StewartFunctions } from '@functions/stewart.service';
import { SubmitFile } from '@models/title-insurance/stewart';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthState } from '@state/auth';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { StewartSelectors } from '@state/global/title-insurance/stewart';
import { SelectedMatter } from '@state/matter/matter.selectors';
import LogRocket from 'logrocket';
import { take } from 'rxjs/operators';
import { ICON } from '@core/util/icon';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { envTypes } from 'src/environments/env.types';
import { environment } from 'src/environments/environment';
import { checkErrorCode } from '@globalUtils/utilities';
import { StewartOAuthLoginComponent } from '@components/stewart-oauth-login/stewart-oauth-login.component';
import { firstValueFrom } from 'rxjs';
import { select, Store } from '@ngxs/store';

export interface StewartModalData {
	submitNewOrder: boolean;
}

enum State {
	SENDING_DATA = 'sendingData',
	UPDATE_FILE = 'updatingFile',
	ERROR = 'error',
	LOADING_PORTAL = 'loadingPortal',
	SHOW_PORTAL = 'showPortal',
	SET_PROFILE_ID = 'setProfileId',
	SET_PROFILE_ID_ERROR = 'setProfileIdError',
}

const isDev = environment.env === envTypes.DEVELOPMENT;

@UntilDestroy()
@Component({
	selector: 'app-stewart-modal',
	templateUrl: './stewart-modal.component.html',
	styleUrls: ['./stewart-modal.component.scss'],
	standalone: false,
})
export class StewartModalComponent implements OnInit {
	store = inject(Store);
	matterId = this.store.selectSnapshot(SelectedMatter.id);
	matterName = this.store.selectSnapshot(SelectedMatter.matterName);
	matterNumber = this.store.selectSnapshot(SelectedMatter.matterNumber);
	payload = this.store.selectSnapshot(StewartSelectors.getStewartDataPayload);
	fileType = this.store.selectSnapshot(SelectedMatter.matterFileType);
	assignedLawyer = this.store.selectSnapshot(SelectedMatter.assignedLawyer);
	lawyers = this.store.selectSnapshot(BusinessUsersState.getLawyers);
	payloadHasChanged = this.store.selectSnapshot(
		StewartSelectors.getPayloadHasChanged,
	);
	userId = this.store.selectSnapshot(AuthState.getUserId);
	thirdPartyId = select(SelectedMatter.titleInsuranceThirdPartyId);
	status = this.store.selectSnapshot(SelectedMatter.getTitleInsuranceState);

	@HostListener('window:beforeunload')
	private async persistStatus() {
		const thirdPartyId = this.thirdPartyId();
		if (thirdPartyId && this.matterId) {
			await firstValueFrom(
				this.stewartFunctions.getStatus_v2(
					this.matterId,
					thirdPartyId,
					this.payload,
				),
			);
		}
	}

	public file: SubmitFile = {} as SubmitFile;
	public state: State = State.SENDING_DATA;
	public ICON = ICON;
	public iframeUrl: SafeResourceUrl;
	public states = State;
	public errorMessages: Message[];
	public savingProfileId: boolean;
	public profileIdSaveError: boolean;
	public profileId: string;
	public submitNewOrder = false;

	public readonly clientId: string;
	private isHandlingError: boolean;

	constructor(
		private stewartFunctions: StewartFunctions,
		private snackbarService: SnackbarService,
		private dialogRef: MatDialogRef<StewartModalComponent>,
		private sanitizer: DomSanitizer,
		private matDialog: MatDialog,
		@Inject(MAT_DIALOG_DATA) public _data: StewartModalData,
	) {
		// todo sc-14852: swap for environment.stewartOAuth.stewartClientId when plugging in v2 functions
		this.clientId = environment.stewartClientId;
	}

	async ngOnInit(): Promise<void> {
		this.dialogRef.disableClose = true;
		this.dialogRef.addPanelClass(['vw-90', 'vh-90']);
		this.submitNewOrder = this._data?.submitNewOrder;

		this.dialogRef
			.backdropClick()
			.pipe(untilDestroyed(this))
			.subscribe(() => this.beforeClose());
		// fetch profileId for the lawyer
		this.profileId = this.lawyers.find(
			(x) => x.id === this.assignedLawyer?.id,
		)?.stewartProfileId;

		if (!this.profileId) {
			this.state = State.SET_PROFILE_ID;
		} else if (this.payloadHasChanged || !this.thirdPartyId()) {
			await this.submitFile();
		} else {
			await this.open();
		}
	}

	async beforeClose() {
		this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
			'http://www.google.com',
		);
		this.state = State.UPDATE_FILE;
		await this.persistStatus();
		this.dialogRef.close();
	}

	async open() {
		try {
			this.state = State.LOADING_PORTAL;
			const token = await firstValueFrom(
				this.stewartFunctions.getToken_v2(this.userId),
			);
			this.setIframeUrl(this.thirdPartyId(), token.token);

			this.state = State.SHOW_PORTAL;
		} catch (err) {
			this.handleError(err, false);
		}
	}

	setIframeUrl(thirdPartyId: string | number, token: string) {
		const url = `${environment.stewartBasePortalUrl}/Transaction/FileContainer/${thirdPartyId}?Integrator=${this.clientId}&token=${token}`;

		if (isDev) {
			window.open(url, '_blank');
		} else {
			this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
		}
	}

	async submitFile() {
		this.state = State.SENDING_DATA;
		this.errorMessages = [];

		try {
			const _payload = this.submitNewOrder
				? { ...this.payload, FileNo: 0 }
				: this.payload;

			LogRocket.log('Payload sent to Stewart', this.payload);

			const result = await firstValueFrom(
				this.stewartFunctions.submitFile_v2(
					this.matterId,
					_payload,
					this.assignedLawyer?.id,
				),
			);

			LogRocket.log('Response from Stewart', result);

			if (!result?.result?.IsValid) {
				this.errorMessages = result?.result?.Messages?.filter(
					(x) => !!x.ErrorCode,
				);

				if (this.errorMessages.length > 0) {
					LogRocket.error('Stewart - error sending payload', result);
					this.state = State.ERROR;

					return;
				}
			}

			this.setIframeUrl(result.fileNo, result.token);
			this.state = State.SHOW_PORTAL;
		} catch (err) {
			this.handleError(err, true);
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	handleError(err: any, resubmit: boolean) {
		if (isDev && this.isHandlingError) {
			return;
		}

		if (isDev) {
			this.isHandlingError = true;
		}

		this.state = State.ERROR;
		if (checkErrorCode(err?.code, ErrorCodes.UNAUTHENTICATED)) {
			this.matDialog
				.open(StewartOAuthLoginComponent)
				.afterClosed()
				.pipe(take(1))
				.subscribe((result) => {
					if (result) {
						if (resubmit) {
							void this.submitFile();
						} else {
							void this.open();
						}
					} else {
						this.dialogRef.close();
					}
				});
		} else if (
			checkErrorCode(err?.code, ErrorCodes.UNKNOWN) &&
			err?.details?.errors
		) {
			this.errorMessages = err?.details?.errors;
			LogRocket.error('Unable to submit filedata to Stewart', err.details);

			const profileErrorMessages = [
				'profile id is required',
				'profile is not authorized.',
			];

			const showSetProfileId = this.errorMessages.some(
				(x) =>
					x.ErrorCode === 3000 ||
					x.ErrorCode === 3002 ||
					(x.ErrorCode === 2000 &&
						x.FieldStates.some((y) =>
							profileErrorMessages.includes(y.Message.toLowerCase()),
						)),
			);

			if (showSetProfileId) {
				this.state = State.SET_PROFILE_ID_ERROR;
			}
		} else {
			this.snackbarService.openSnackBar({
				text: 'There was an error when attempting submit the file data to Stewart Title',
				type: 'error',
			});
			LogRocket.error('Unable to submit file data to Stewart', err);
		}
	}

	async saveProfileId(form: NgForm) {
		if (form.valid) {
			this.profileIdSaveError = false;
			this.savingProfileId = true;
			try {
				await firstValueFrom(
					this.stewartFunctions.setProfileId(
						this.assignedLawyer.id,
						this.profileId,
					),
				);
				await this.submitFile();
			} catch (err) {
				this.profileIdSaveError = true;
				LogRocket.log('STEWART - error attempting to save the profileId', err);
			} finally {
				this.savingProfileId = false;
			}
		}
	}
}
